import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	BadgeComponent,
	CollapseComponent,
	DividerComponent,
	EmptyStateComponent,
	InfoGroupComponent,
} from '@tecalisdev/infinity';
import { PhoneDisplayComponent } from '../../../../../shared/components/phone-display/phone-display.component';
import { notAvailableLabel } from '../../../../../core/utils/utils';
import {
	IOperationDetailResponse,
	IOperationTfaMessagingDetailData,
	IOperationTfaSiblingSession,
	isTfaOperationDetail,
	OPERATION_TYPE,
} from '../../../../models/operations.models';
import { IOperationTfaCommunication, IProcessSentMessage } from '../../../../../processes/models/processes.models';
import {
	formatSentMessageStatusLabel,
	resolveSentMessageContent,
} from '../../../../../processes/utils/sent-messages.util';
import {
	resolveTfaChannel,
	resolveTfaChannelLabelKey,
	resolveTfaCommunicationChannel,
	resolveTfaCommunicationTypeLabel,
} from '../../../../utils/tfa-display.util';
import { OperationDetailOperationMetaComponent } from '../../../../../shared/components/operations/common/operation-detail-operation-meta/operation-detail-operation-meta.component';

type MessagingDetailsCollapseSection = 'delivery' | 'communications' | 'siblings' | 'operation';

@Component({
	selector: 'app-operation-detail-tabs-details-messaging',
	imports: [
		TranslatePipe,
		DatePipe,
		CollapseComponent,
		EmptyStateComponent,
		InfoGroupComponent,
		BadgeComponent,
		DividerComponent,
		PhoneDisplayComponent,
		OperationDetailOperationMetaComponent,
	],
	templateUrl: './operation-detail-tabs-details-messaging.component.html',
	styleUrl: './operation-detail-tabs-details-messaging.component.scss',
})
export class OperationDetailTabsDetailsMessagingComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;

	private trans = inject(TranslateService);

	public messagingData: IOperationTfaMessagingDetailData | null = null;
	public expandedCollapseSection: MessagingDetailsCollapseSection | null = 'delivery';
	public OPERATION_TYPE = OPERATION_TYPE;

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) return;
		this.messagingData = isTfaOperationDetail(this.detail)
			? (this.detail.data as IOperationTfaMessagingDetailData)
			: null;
	}

	public toggleCollapse(section: MessagingDetailsCollapseSection, expanded?: boolean): void {
		this.expandedCollapseSection = !expanded ? null : section;
	}

	public channelLabel(): string {
		const channel = resolveTfaChannel(this.messagingData ?? {});
		const key = resolveTfaChannelLabelKey(channel);
		if (key) {
			return this.trans.instant(key);
		}
		return channel || notAvailableLabel(this.trans);
	}

	/** Estado de entrega del log 2FA más reciente (no confundir con OTP validado). */
	public deliveryStatusLabel(): string {
		const latest = this.latestCommunication();
		if (!latest) {
			return notAvailableLabel(this.trans);
		}
		const label = formatSentMessageStatusLabel(this.asSentMessage(latest), (k) => this.trans.instant(k));
		return label || notAvailableLabel(this.trans);
	}

	/** OTP introducido/validado en web (`tfa.success`). */
	public codeValidatedLabel(): string {
		const success = this.messagingData?.success;
		if (success === undefined || success === null) {
			return notAvailableLabel(this.trans);
		}
		return success
			? this.trans.instant('OPERATIONS.DETAIL.MESSAGING.SUCCESS_YES')
			: this.trans.instant('OPERATIONS.DETAIL.MESSAGING.SUCCESS_NO');
	}

	public attemptsLabel(): string {
		const attempts = this.messagingData?.attempts;
		if (attempts === undefined || attempts === null) {
			return notAvailableLabel(this.trans);
		}
		return String(attempts);
	}

	public totalTriesLabel(): string {
		const tries = this.detail?.total_tries ?? this.messagingData?.attempts;
		if (tries === undefined || tries === null) {
			return notAvailableLabel(this.trans);
		}
		return String(tries);
	}

	public communications(): IOperationTfaCommunication[] {
		return this.messagingData?.communications ?? [];
	}

	public siblingSessions(): IOperationTfaSiblingSession[] {
		return this.messagingData?.sibling_tfa_sessions ?? [];
	}

	public communicationChannelLabel(communication: IOperationTfaCommunication): string {
		const channel = resolveTfaCommunicationChannel(communication);
		const key = resolveTfaChannelLabelKey(channel);
		if (key) {
			return this.trans.instant(key);
		}
		return channel;
	}

	public communicationTypeLabel(communication: IOperationTfaCommunication): string {
		return resolveTfaCommunicationTypeLabel(communication.type);
	}

	public communicationStatusLabel(communication: IOperationTfaCommunication): string {
		const label = formatSentMessageStatusLabel(this.asSentMessage(communication), (k) => this.trans.instant(k));
		return label || notAvailableLabel(this.trans);
	}

	public communicationMessageLabel(communication: IOperationTfaCommunication): string {
		const content = resolveSentMessageContent(this.asSentMessage(communication));
		return content || this.trans.instant('PROCESSES.DETAIL.MESSAGES.CONTENT_UNAVAILABLE');
	}

	public siblingChannelLabel(session: IOperationTfaSiblingSession): string {
		const channel = session.channel?.trim().toLowerCase() ?? '';
		const key = resolveTfaChannelLabelKey(channel);
		if (key) {
			return this.trans.instant(key);
		}
		return channel || notAvailableLabel(this.trans);
	}

	public showCommunicationsSection(): boolean {
		return this.communications().length > 0;
	}

	public showSiblingSessionsSection(): boolean {
		return this.siblingSessions().length > 0;
	}

	/** Comunicación más reciente por `created_at` (fallback: primera del array). */
	private latestCommunication(): IOperationTfaCommunication | null {
		const list = this.communications();
		if (list.length === 0) {
			return null;
		}
		return [...list].sort((a, b) => {
			const aMs = a.created_at ? new Date(a.created_at).getTime() : 0;
			const bMs = b.created_at ? new Date(b.created_at).getTime() : 0;
			return bMs - aMs;
		})[0];
	}

	private asSentMessage(communication: IOperationTfaCommunication): IProcessSentMessage {
		return {
			id: communication.id,
			phone: communication.phone,
			channel: communication.channel as IProcessSentMessage['channel'],
			type: communication.type,
			status: communication.status ?? undefined,
			message: communication.message ?? undefined,
			created_at: communication.created_at,
		};
	}
}
